import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  sampleTypesApi,
  type SampleAdditionalField,
  type SampleTypeDefinition,
  type SampleTypeField,
} from '../../api/endpoints.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  DateField,
  DateTimeField,
  SelectField,
  TextAreaField,
  TextField,
} from '../../components/ui/index.js';
import { formatDate, formatDateTime, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * A laboratory's own sample types, and the additional intake fields each one
 * asks for (0024, decisions D296-D305), as every sample page reads them.
 *
 * SAMPLE INTAKE IS SCHEMA-DRIVEN. No page branches on a material: the selected
 * type's active fields are rendered from its definition, and a type a
 * laboratory adds tomorrow appears on the receiving form without a release.
 * Test execution is a different concept and never reads these fields.
 *
 * READ CLAUDE.md SECTION 4. These fields describe a delivery. None is a reading.
 */

type Locale = ReturnType<typeof currentLocale>;

/** The query every sample page shares: one request, cached, whatever the card count. */
export const SAMPLE_TYPES_QUERY_KEY = ['sample-types'] as const;

/** A label in the interface language, falling back to the other, never to an id. */
export function pickLabel(ar: string | null | undefined, en: string | null | undefined, locale: Locale): string {
  const first = locale === 'ar' ? ar : en;
  const second = locale === 'ar' ? en : ar;
  return (first && first.trim()) || (second && second.trim()) || '';
}

export function useSampleTypes() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const query = useQuery({
    queryKey: SAMPLE_TYPES_QUERY_KEY,
    queryFn: () => sampleTypesApi.list(),
    staleTime: 60_000,
  });
  const types = useMemo(() => query.data ?? [], [query.data]);
  const byCode = useMemo(() => new Map(types.map((type) => [type.code, type])), [types]);

  /** The laboratory's name for a type code; the built-in label, then the code, while loading. */
  const nameOf = useCallback(
    (code: string) => {
      const type = byCode.get(code);
      const named = type ? pickLabel(type.nameAr, type.nameEn, locale) : '';
      return named || t(`sampleType.${code}`, { defaultValue: code });
    },
    [byCode, locale, t],
  );

  return { ...query, types, byCode, nameOf };
}

/** The active fields a new receiving form shows for a type, in order. */
export function activeFields(type: SampleTypeDefinition | undefined): SampleTypeField[] {
  return (type?.fields ?? []).filter((field) => !field.archived);
}

/** The text a form holds for each field, by field id. Empty is no answer. */
export type AdditionalDraft = Readonly<Record<string, string>>;

/** A stored value as the form's text: options by id, yes/no as `true`/`false`. */
export function draftFromStored(values: readonly SampleAdditionalField[]): Record<string, string> {
  return Object.fromEntries(values.map((value) => [value.fieldId, String(value.value)]));
}

/** One field's form text as the API takes it; null when empty. */
export function payloadValue(field: SampleTypeField, raw: string | undefined): string | boolean | null {
  const text = (raw ?? '').trim();
  if (text === '') return null;
  if (field.fieldType === 'boolean') return text === 'true';
  return field.fieldType === 'long_text' || field.fieldType === 'text' ? (raw ?? '').trim() : text;
}

/**
 * The details to send for a type: only its own ACTIVE fields, only answered
 * ones. A value typed for another type, or for an archived field, stays on
 * the card and is never sent (D298).
 */
export function additionalPayload(
  type: SampleTypeDefinition | undefined,
  draft: AdditionalDraft | undefined,
): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const field of activeFields(type)) {
    const value = payloadValue(field, draft?.[field.id]);
    if (value !== null) out[field.id] = value;
  }
  return out;
}

/** A field's type, as a screen reader and the builder announce it. */
export function fieldTypeLabel(t: (key: string) => string, fieldType: string): string {
  return t(`sampleTypes.fieldType.${fieldType}`);
}

/** The words for a server's refusal of one detail. */
export function additionalErrorText(t: (key: string, options?: Record<string, unknown>) => string, code: string): string {
  return t(`sampleTypes.valueError.${code}`, { defaultValue: t('sampleTypes.valueError.invalid_value') });
}

/**
 * The inputs of a type's additional fields. Rendered from the definition, one
 * control per data type; required ones are marked and announced.
 */
export function AdditionalFieldsInputs({
  fields,
  values,
  onChange,
  errors = {},
  storedOptions = {},
  readOnlyIds = new Set<string>(),
}: {
  readonly fields: readonly SampleTypeField[];
  readonly values: AdditionalDraft;
  readonly onChange: (fieldId: string, value: string) => void;
  readonly errors?: Readonly<Record<string, string>>;
  /** Archived options a stored value still names, so it reads correctly. */
  readonly storedOptions?: Readonly<Record<string, string>>;
  /** Archived fields shown for their historical value, not editable. */
  readonly readOnlyIds?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();

  return (
    <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
      {fields.map((field) => {
        const label = pickLabel(field.labelAr, field.labelEn, locale) || field.key;
        const placeholder = pickLabel(field.placeholderAr, field.placeholderEn, locale) || undefined;
        const help = pickLabel(field.helpAr, field.helpEn, locale);
        const readOnly = readOnlyIds.has(field.id);
        const description = readOnly ? t('sampleTypes.archivedFieldValue') : help || undefined;
        const value = values[field.id] ?? '';
        const common = {
          label,
          isRequired: field.isRequired && !readOnly,
          isDisabled: readOnly,
          ...(description ? { description } : {}),
          ...(errors[field.id] ? { error: errors[field.id] } : {}),
        };
        const set = (next: string | null) => onChange(field.id, next ?? '');
        const wide = field.fieldType === 'long_text' ? 'sm:col-span-2' : '';
        let control;
        switch (field.fieldType) {
          case 'long_text':
            control = <TextAreaField {...common} value={value} onChange={set} rows={2} {...(placeholder ? { placeholder } : {})} />;
            break;
          case 'number':
            control = (
              <TextField
                {...common}
                value={value}
                onChange={set}
                textDirection="ltr"
                {...(placeholder ? { placeholder } : {})}
              />
            );
            break;
          case 'date':
            control = <DateField {...common} value={value || null} onChange={set} />;
            break;
          case 'time':
            control = <TextField {...common} type="time" value={value} onChange={set} textDirection="ltr" />;
            break;
          case 'datetime':
            control = <DateTimeField {...common} value={value || null} onChange={set} timeZone={timeZone} />;
            break;
          case 'select': {
            const options = field.options
              .filter((option) => !option.archived || option.id === value)
              .map((option) => ({
                value: option.id,
                label:
                  (pickLabel(option.labelAr, option.labelEn, locale) || option.value) +
                  (option.archived ? ` (${t('sampleTypes.archivedTag')})` : ''),
                isDisabled: option.archived,
              }));
            if (value && !options.some((option) => option.value === value) && storedOptions[value]) {
              options.push({ value, label: storedOptions[value] as string, isDisabled: true });
            }
            control = (
              <SelectField
                {...common}
                value={value || null}
                onChange={set}
                options={options}
                placeholder={t('sampleTypes.noAnswer')}
              />
            );
            break;
          }
          case 'boolean':
            // Yes, No or not stated: an unticked box would turn "nobody said"
            // into "no" (CLAUDE.md section 8).
            control = (
              <SelectField
                {...common}
                value={value || null}
                onChange={set}
                options={[
                  { value: 'true', label: t('common.yes') },
                  { value: 'false', label: t('common.no') },
                ]}
                placeholder={t('sampleTypes.noAnswer')}
              />
            );
            break;
          default:
            control = <TextField {...common} value={value} onChange={set} {...(placeholder ? { placeholder } : {})} />;
        }
        return (
          <div key={field.id} className={`min-w-0 ${wide}`} data-testid={`additional-field-${field.key}`}>
            {control}
          </div>
        );
      })}
    </div>
  );
}

/** A stored value as words, in the interface language. */
export function displayAdditionalValue(
  value: SampleAdditionalField,
  locale: Locale,
  timeZone: string | null,
  t: (key: string) => string,
): string {
  switch (value.fieldType) {
    case 'select':
      return value.option ? pickLabel(value.option.labelAr, value.option.labelEn, locale) || value.option.value : '';
    case 'boolean':
      return value.value === true ? t('common.yes') : t('common.no');
    case 'date':
      return formatDate(String(value.value), { locale, timezone: 'UTC' }) ?? String(value.value);
    case 'datetime':
      return formatDateTime(String(value.value), { locale, ...(timeZone ? { timezone: timeZone } : {}) }) ?? String(value.value);
    case 'number':
      return formatNumber(Number(value.value), { locale }) ?? String(value.value);
    default:
      return String(value.value);
  }
}

/** A sample's additional details, read-only, under the labels they were stored with. */
export function AdditionalFieldsList({ values }: { readonly values: readonly SampleAdditionalField[] }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();
  if (values.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 gap-x-gutter gap-y-stack sm:grid-cols-2" data-testid="additional-details">
      {values.map((value) => (
        <div key={value.fieldId} className="min-w-0">
          <dt className="text-caption text-default-600">
            {pickLabel(value.labelAr, value.labelEn, locale) || value.key}
            {value.archived ? ` (${t('sampleTypes.archivedTag')})` : ''}
          </dt>
          <dd className="mt-0.5 break-words text-body text-default-900">
            {displayAdditionalValue(value, locale, timeZone, t)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A type code shown by the laboratory's own name for it, in the interface language. */
export function SampleTypeName({ code }: { readonly code: string }) {
  const { nameOf } = useSampleTypes();
  return <>{nameOf(code)}</>;
}
